import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import type { InventoryRuleMode, ProductInventoryRuleDTO, ProductInventoryRuleHistoryDTO } from "./product";
import type { SigmClientDTO } from "./reporting";

/** How a channel receives its feed. */
export type SalesChannelTransport = "API" | "MAIL" | "EDI";

/** Which writer builds a channel's feed — the one attribute of a channel that is code. */
export type InventoryFeedFormat =
  | "WAYFAIR_API"
  | "SHOPIFY_API"
  | "OVERSTOCK_API"
  | "BISON_XLSX"
  | "LINEN_CHEST_XLSX"
  | "MEUBLES_RD_XLSX"
  | "STAPLES_XLSX"
  | "SIMONS_CSV"
  | "AMAZON_VENDOR_CSV"
  | "TANGUAY_XLSX"
  | "TEAM_US_XLSX"
  | "MAIL_LIST_XLSX";

/**
 * A platform an inventory feed targets. `client` and `market` are read-only
 * on the wire: the server attaches the SIGM client and derives the market
 * from its currency (CAD → CA, USD → US). A channel whose client cannot be
 * found comes back with `market: null` and cannot run.
 */
export interface SalesChannelDTO {
  id: number;
  code: string;
  name: string;
  nexera_client_id: number;
  transport: SalesChannelTransport;
  feed_format: InventoryFeedFormat;
  active: boolean;
  sort_order: number;
  external_supplier_id?: string | null;
  external_location_id?: string | null;
  required_metafield?: string | null;
  mail_to: string[];
  mail_cc: string[];
  mail_bcc: string[];
  mail_subject?: string | null;
  mail_attachment?: string | null;
  /** Successful runs that send a DISCONTINUED product before its rule is delisted; null = never (Staples wants its full list every time). */
  delist_after_sends?: number | null;
  /** The floor the inventory rule job gives a top seller here; null = this channel never floors. */
  default_floor_quantity?: number | null;
  /** A product created later starts listed here; off where the catalogue is chosen by hand (Amazon). */
  is_listing_new_products: boolean;
  client?: SigmClientDTO | null;
  market?: "CA" | "US" | null;
}

export class SalesChannelClient extends ResourceClient<SalesChannelDTO> {
  constructor(http: HttpClient) {
    super(http, "/sales-channel");
  }
}

/** Rule-level calls that are not part of the product save. */
export class ProductInventoryRuleClient extends ResourceClient<ProductInventoryRuleDTO> {
  constructor(http: HttpClient) {
    super(http, "/product-inventory-rule");
  }

  /** Clears the delisted flag and the counter; the SKU goes out again until the channel's threshold. */
  relist(id: number): Promise<ProductInventoryRuleDTO> {
    return this.http.post<ProductInventoryRuleDTO>(`/product-inventory-rule/${id}/relist`);
  }

  /**
   * The same change on every (product, channel) pair. Each field left null
   * keeps what the rule has; the server applies the hard rules on top.
   * Returns the ids of the products that changed.
   */
  bulk(productIds: number[], salesChannelIds: number[], change: InventoryRuleBulkChange): Promise<number[]> {
    return this.http.post<number[]>("/product-inventory-rule/bulk", {
      product_ids: productIds,
      sales_channel_ids: salesChannelIds,
      mode: change.mode,
      floor_quantity: change.mode === "FLOOR" ? change.floor_quantity : null,
      is_listed: change.is_listed,
      is_locked: change.is_locked,
    });
  }

  /** What the inventory rule job would change now. Writes nothing. */
  previewAutomation(): Promise<InventoryRulePreviewDTO> {
    return this.http.get<InventoryRulePreviewDTO>("/product-inventory-rule/automation/preview");
  }

  /** Runs the job now, whatever the daily switch says. */
  runAutomation(): Promise<InventoryRuleRunDTO> {
    return this.http.post<InventoryRuleRunDTO>("/product-inventory-rule/automation/run");
  }

  history(productId: number): Promise<ProductInventoryRuleHistoryDTO[]> {
    return this.http.get<ProductInventoryRuleHistoryDTO[]>("/product-inventory-rule/history", {
      query: { product_id: String(productId) },
    });
  }
}

/** A bulk edit: null keeps what each rule has. */
export interface InventoryRuleBulkChange {
  mode: InventoryRuleMode | null;
  floor_quantity: number | null;
  is_listed: boolean | null;
  is_locked: boolean | null;
}

/** Why the job wants a rule the way it is. */
export type InventoryRuleAutomationReason =
  | "COMPONENT"
  | "DISCONTINUED"
  | "NOT_LAUNCHED"
  | "PENDING_DISCONTINUED"
  | "TOP_SELLER"
  | "NEW_PRODUCT"
  | "IN_PRODUCTION"
  | "DEFAULT"
  | "LOCKED";

/** Counts a run records (and a preview predicts). */
export interface InventoryRuleCounts {
  to_floor_count: number;
  to_dispatchable_count: number;
  to_blocked_count: number;
  unlisted_count: number;
  /** Rules missing for a (product, channel) pair; should always be 0. */
  created_count: number;
  /** Locked rules the job would otherwise have changed. */
  locked_skipped_count: number;
}

/** One rule the job would change; with `is_held_by_lock`, the new_* values are what it would set but for the lock. */
export interface InventoryRuleChangeDTO {
  product_inventory_rule_id: number;
  product_id: number;
  sku?: string | null;
  sales_channel_id: number;
  old_mode: InventoryRuleMode;
  old_floor_quantity?: number | null;
  old_is_listed: boolean;
  new_mode: InventoryRuleMode;
  new_floor_quantity?: number | null;
  new_is_listed: boolean;
  reason: InventoryRuleAutomationReason;
  is_held_by_lock: boolean;
}

export interface InventoryRulePreviewDTO extends InventoryRuleCounts {
  floor_top_ca: number;
  floor_top_us: number;
  changes: InventoryRuleChangeDTO[];
}

/** One run of the job. */
export interface InventoryRuleRunDTO extends InventoryRuleCounts {
  id: number;
  started_at: string;
  finished_at?: string | null;
  source: "KPI" | "MANUAL";
  account_id?: number | null;
  is_successful: boolean;
  error?: string | null;
  floor_top_ca: number;
  floor_top_us: number;
}

export class InventoryRuleRunClient extends ResourceClient<InventoryRuleRunDTO> {
  constructor(http: HttpClient) {
    super(http, "/inventory-rule-run");
  }
}

/**
 * Why a feed published the quantity it did for one SKU — the first gate that
 * fired, in the resolver's order.
 */
export const INVENTORY_REASONS = [
  "UNKNOWN_SKU",
  "DELISTED",
  "NOT_LISTED",
  "DISCONTINUED",
  "NOT_LAUNCHED",
  "NOT_SOLD_IN_MARKET",
  "NO_RULE",
  "BLOCKED",
  "ITEM_BLOCKED",
  "OUT_OF_STOCK",
  "FLOORED",
  "DISPATCHABLE",
] as const;
export type InventoryReason = (typeof INVENTORY_REASONS)[number];

/** Reasons that mean "we chose not to publish stock", as opposed to "there is none" or "we published". */
export function isGatedReason(reason: InventoryReason): boolean {
  return reason !== "DISPATCHABLE" && reason !== "FLOORED" && reason !== "OUT_OF_STOCK";
}

/** One feed run for one channel. */
export interface InventoryUpdateDTO {
  id: number;
  created_at?: string | null;
  completed_at?: string | null;
  sales_channel_id: number;
  sales_channel_code?: string | null;
  sales_channel_name?: string | null;
  api_request?: string | null;
  api_response?: string | null;
  api_response_http_status?: number | null;
  api_response_success?: boolean | null;
  line_count?: number | null;
}

/** What a run published for one SKU, and why. `product_id` is null for a SKU the platform holds and we do not. */
export interface InventoryUpdateLineDTO {
  id: number;
  inventory_update_id: number;
  product_id?: number | null;
  sku: string;
  quantity: number;
  reason: InventoryReason;
  discontinued: boolean;
  /** For a bundle: the single item that blocked it or limits its quantity. */
  detail?: string | null;
}

/** A preview line adds what the warehouses could ship, beside what would be published. */
export interface InventoryPreviewLineDTO {
  product_id?: number | null;
  sku: string;
  quantity: number;
  dispatchable: number;
  reason: InventoryReason;
  discontinued: boolean;
  /** For a bundle: the single item that blocked it or limits its quantity. */
  detail?: string | null;
}

/**
 * What one channel would receive for one product right now, worked out as its
 * feed would. For a bundle, `detail` names the single item that blocks it
 * (reason ITEM_BLOCKED) or sets its quantity.
 */
export interface ProductChannelDecisionDTO {
  sales_channel_id: number;
  quantity: number;
  dispatchable: number;
  reason: InventoryReason;
  detail?: string | null;
  /** False when the channel's file leaves the product out (not listed, delisted, or no required metafield). */
  is_in_feed: boolean;
  /** The channel's required metafield when the product lacks it, e.g. amazon_vendor_ca_asin. */
  missing_metafield?: string | null;
}

/** A dry run: nothing delivered, nothing recorded. */
export interface InventoryPreviewDTO {
  sales_channel: SalesChannelDTO;
  request?: string | null;
  /** Set for MAIL channels; the file itself comes from `previewAttachment`. */
  attachment_name?: string | null;
  lines: InventoryPreviewLineDTO[];
}

export class InventoryUpdateClient extends ResourceClient<InventoryUpdateDTO> {
  constructor(http: HttpClient) {
    super(http, "/inventory-update");
  }

  /** Runs the channel's feed for real: delivers, records the run and its lines. */
  updateChannel(code: string): Promise<InventoryUpdateDTO> {
    return this.http.post<InventoryUpdateDTO>(`/inventory-update/update/${code}`);
  }

  preview(code: string): Promise<InventoryPreviewDTO> {
    return this.http.get<InventoryPreviewDTO>(`/inventory-update/preview/${code}`);
  }

  previewAttachment(code: string): Promise<{ blob: Blob; filename: string }> {
    return this.http.download(`/inventory-update/preview/${code}/attachment`);
  }

  /** What every channel would receive for one product right now. */
  productDecisions(productId: number): Promise<ProductChannelDecisionDTO[]> {
    return this.http.get<ProductChannelDecisionDTO[]>(`/inventory-update/product/${productId}`);
  }

  lines(inventoryUpdateId: number): Promise<InventoryUpdateLineDTO[]> {
    return this.http.get<InventoryUpdateLineDTO[]>("/inventory-update-line", {
      query: { inventory_update_id: String(inventoryUpdateId) },
    });
  }
}
